import qs from "qs";
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import { useToasts } from "@/components/ui/Feedback/Toast";

import { ENV_KEY } from "./environment";

declare var OfficeRuntime: any;

// Token caching to prevent unnecessary async calls
let cachedToken: null | string = null;
let tokenPromise: null | Promise<null | string> = null;

// Function to refresh token cache when needed
export function refreshAuthToken() {
  cachedToken = null;
  tokenPromise = null;
}

async function getCachedToken(): Promise<null | string> {
  if (cachedToken !== null) {
    return cachedToken;
  }

  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = OfficeRuntime.storage.getItem("token");
  cachedToken = await tokenPromise;
  tokenPromise = null;
  return cachedToken;
}

const axios = Axios.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-Application-Release": localStorage.getItem("_app_version"),
  },
});

// Set up request interceptor for authentication
axios.interceptors.request.use(async (config) => {
  const token = await getCachedToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Configure params serializer
  config.paramsSerializer = (params) => {
    return qs.stringify(params, {
      arrayFormat: "brackets",
      encode: false,
    });
  };

  return config;
});

export interface Response<T> {
  data: T;
  meta?: any;
}

interface Params {
  [name: string]: string;
}

export function registerAppVersionInterceptor(
  callback: (beVersion: string) => void
) {
  axios.interceptors.response.use((response) => {
    callback(response.headers["x-application-release"]);

    return response;
  });
}

export function registerErrorResponseInterceptor(
  callback: (response: AxiosResponse) => void
) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      callback(error.response);
      return Promise.reject(error);
    }
  );
}

export function registerNetworkErrorInterceptor(
  callback: (isNetworkError: boolean) => void
) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Check if this is a network error (no response received)
      const isNetworkError =
        !error.response &&
        (error.code === "NETWORK_ERROR" ||
          error.code === "ERR_NETWORK" ||
          error.message === "Network Error" ||
          error.code === "ECONNABORTED");

      callback(isNetworkError);
      return Promise.reject(error);
    }
  );
}

export const http = {
  delete<T>(url: string): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);
    return axios
      .delete<Response<T>>(instance + url, { withCredentials: true })
      .then((response) => response.data);
  },

  async get<T>(
    url: string,
    params: Params = {},
    options: AxiosRequestConfig = {}
  ): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);

    let opt = Object.assign({}, options, params);
    return axios
      .get<Response<T>>(instance + url, opt)
      .then((response) => response.data);
  },

  head<T>(url: string, data?: any): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);
    return axios
      .head<Response<T>>(instance + url, data)
      .then((response) => response.data);
  },

  patch<T>(url: string, data?: any): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);
    return axios
      .patch<Response<T>>(instance + url, data)
      .then((response) => response.data);
  },

  post<T>(url: string, data?: any): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);
    return axios.post<Response<T>>(instance + url, data).then((response) => {
      return response.data;
    });
  },

  put<T>(url: string, data?: any): Promise<Response<T>> {
    const instance = localStorage.getItem(ENV_KEY);
    return axios
      .put<Response<T>>(instance + url, data, { withCredentials: true })
      .then((response) => response.data);
  },
};

if (process.env.NODE_ENV !== "production") {
  axios.interceptors.response.use(
    (response) => {
      if (
        response.status === 200 &&
        typeof response.data === "string" &&
        response.data?.includes("window.Sfdump")
      ) {
        renderDebugView(response.data);
      }

      return response;
    },
    (error) => {
      if (error.response?.status === 500 && error.response?.data?.html) {
        renderDebugView(error.response.data.html);
      }

      return Promise.reject(error);
    }
  );
}

function renderDebugView(html: string): void {
  const doc = document.open("text/html", "replace");
  doc.write(html);
  doc.close();
}
